/**
 * GoogleReviews — Public component that renders REAL Google Maps reviews for
 * Restaurante Museo Tenería, fetched from our backend `/api/reviews` endpoint
 * (which proxies + caches Google Places API for 12 h).
 *
 * The Home variant ALSO renders a "Reseñas destacadas" strip with curated
 * quotes from `data/curatedReviews.js` so the page always opens with a strong
 * social-proof anchor — even on the rare chance that the Google API is down
 * (in which case the curated quotes also act as the fallback grid).
 *
 * Usage:
 *   <GoogleReviews variant="full" />     // Home — curated + 3 Google reviews
 *   <GoogleReviews variant="compact" />  // Reservas — rating + 1 short quote
 *
 * Why we proxy through the backend:
 *  - The Google API key never reaches the browser.
 *  - The 12h MongoDB TTL cache keeps us inside Google's free tier.
 *
 * The component handles loading (skeleton), error (silent fallback to curated
 * reviews), and success states. Google review API returns at most 5 reviews.
 */
import { useEffect, useState } from "react";
import { Star, ExternalLink, Quote } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { CURATED_REVIEWS } from "@/data/curatedReviews";

/** Render a 5-star rating row (filled stars + half-star approximation). */
function StarRow({ value = 0, size = 14 }) {
    const v = Number(value) || 0;
    return (
        <div className="inline-flex items-center gap-0.5 text-gold" aria-label={`${v} de 5`}>
            {[0, 1, 2, 3, 4].map((i) => (
                <Star
                    key={i}
                    size={size}
                    fill={i + 0.5 <= v ? "currentColor" : "none"}
                    strokeWidth={i + 0.5 <= v ? 0 : 1.2}
                />
            ))}
        </div>
    );
}

/** Short formatted date helper, defensive against bad input. */
function formatDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return "";
    }
}

/** Curated featured-quotes strip — always rendered on Home (full variant). */
function CuratedStrip({ items = CURATED_REVIEWS }) {
    if (!items?.length) return null;
    return (
        <div className="mt-20 md:mt-28 pt-16 md:pt-20 border-t border-gold/15">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="text-center mb-12 md:mb-16"
            >
                <p className="font-editorial italic text-[0.65rem] tracking-[0.3em] md:tracking-[0.4em] uppercase text-gold mb-4">
                    — Selección de la casa —
                </p>
                <h3 className="font-display text-2xl sm:text-3xl md:text-5xl text-ivory font-light leading-tight">
                    Reseñas <span className="italic text-gold">destacadas</span>
                </h3>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5 md:gap-6">
                {items.slice(0, 5).map((q, i) => (
                    <motion.figure
                        key={i}
                        initial={{ opacity: 0, y: 18 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-40px" }}
                        transition={{ duration: 0.85, delay: Math.min(i * 0.08, 0.4) }}
                        className="border border-gold/20 bg-bg/40 backdrop-blur-sm p-5 md:p-6 flex flex-col"
                        data-testid="curated-review-card"
                    >
                        <Quote
                            size={16}
                            className="text-gold/50 mb-3"
                            strokeWidth={1.5}
                            aria-hidden
                        />
                        <blockquote className="font-body text-sm text-ivory leading-relaxed italic line-clamp-7">
                            {q.text}
                        </blockquote>
                        <figcaption className="mt-5 pt-4 border-t border-gold/10">
                            <p className="font-editorial italic text-sm text-gold leading-tight">
                                — {q.author}
                            </p>
                            <p className="font-editorial italic text-[0.65rem] tracking-[0.18em] uppercase text-stone mt-1 leading-tight">
                                {q.relative_time}
                                {q.source ? ` · ${q.source}` : ""}
                            </p>
                        </figcaption>
                    </motion.figure>
                ))}
            </div>
        </div>
    );
}

export default function GoogleReviews({ variant = "full", limit = 3 }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.get("/reviews")
            .then((r) => !cancelled && setData(r.data))
            .catch(() => !cancelled && setErrored(true))
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, []);

    // Loading skeleton — keep layout stable so the page doesn't jump.
    if (loading) {
        return (
            <div className="w-full max-w-2xl mx-auto py-12 px-4">
                <div className="h-6 w-48 bg-bg-alt animate-pulse mb-4" />
                <div className="h-24 w-full bg-bg-alt animate-pulse" />
            </div>
        );
    }

    const hasGoogle = !errored && data && data.reviews?.length;
    const reviews = hasGoogle ? data.reviews.slice(0, limit) : [];
    const total = data?.user_rating_count || 0;
    const rating = data?.rating;

    /* ============== COMPACT VARIANT — for /reservas etc. =============== */
    if (variant === "compact") {
        // Use the first curated quote as fallback if Google has nothing.
        const first = hasGoogle ? reviews[0] : CURATED_REVIEWS[0];
        if (!first) return null;
        return (
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9 }}
                className="border border-gold/20 bg-bg-alt p-6 md:p-8 max-w-2xl mx-auto"
                data-testid="google-reviews-compact"
            >
                {hasGoogle && (
                    <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-display italic text-2xl text-gold leading-none">
                            {rating?.toFixed(1).replace(".", ",")}
                        </p>
                        <StarRow value={rating} size={14} />
                        <p className="font-editorial italic text-xs tracking-[0.2em] uppercase text-stone">
                            {total.toLocaleString("es-ES")} reseñas en Google
                        </p>
                    </div>
                )}
                {first?.text && (
                    <blockquote className="mt-5 font-body text-sm md:text-base text-ivory italic leading-relaxed line-clamp-4">
                        <Quote
                            size={14}
                            className="inline mr-1 -mt-1 text-gold/60"
                            strokeWidth={1.5}
                        />
                        {first.text}
                    </blockquote>
                )}
                {first?.author && (
                    <p className="font-editorial italic text-[0.7rem] tracking-[0.2em] uppercase text-stone mt-3">
                        — {first.author}
                    </p>
                )}
                {data?.google_maps_uri && (
                    <a
                        href={data.google_maps_uri}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-2 mt-5 text-gold font-editorial italic text-sm link-underline"
                        data-testid="google-reviews-link"
                    >
                        Ver todas en Google Maps <ExternalLink size={12} />
                    </a>
                )}
            </motion.div>
        );
    }

    /* ============== FULL VARIANT — for Home ============================ */
    // If Google has nothing, render only the curated strip wrapped in the
    // same section shell so the page always shows social proof.
    return (
        <section
            className="relative py-24 md:py-36 bg-bg-alt border-t border-gold/10"
            data-testid="google-reviews-section"
        >
            <div className="absolute inset-0 paper-grain pointer-events-none" />
            <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12">
                {hasGoogle && (
                    <>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1 }}
                            className="text-center mb-16 md:mb-20"
                        >
                            <p className="font-editorial italic text-[0.65rem] tracking-[0.3em] md:tracking-[0.4em] uppercase text-gold mb-4">
                                — Lo que dicen los comensales —
                            </p>
                            <h2 className="font-display text-3xl sm:text-4xl md:text-6xl text-ivory font-light leading-tight">
                                Reseñas <span className="italic text-gold">en Google</span>
                            </h2>
                            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 sm:gap-5">
                                <span className="font-display italic text-4xl md:text-5xl text-gold leading-none">
                                    {rating?.toFixed(1).replace(".", ",")}
                                </span>
                                <StarRow value={rating} size={16} />
                                <span className="font-editorial italic text-xs sm:text-sm tracking-[0.15em] sm:tracking-[0.25em] uppercase text-stone">
                                    {total.toLocaleString("es-ES")} reseñas
                                </span>
                            </div>
                        </motion.div>

                        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                            {reviews.map((r, i) => (
                                <motion.article
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-40px" }}
                                    transition={{
                                        duration: 0.9,
                                        delay: Math.min(i * 0.12, 0.4),
                                    }}
                                    className="border border-gold/15 bg-bg/40 backdrop-blur-sm p-6 md:p-7 flex flex-col"
                                    data-testid="google-review-card"
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            {r.profile_photo ? (
                                                <img
                                                    src={r.profile_photo}
                                                    alt={r.author || "Reseña Google"}
                                                    referrerPolicy="no-referrer"
                                                    className="w-9 h-9 rounded-full object-cover border border-gold/30 shrink-0"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <span className="w-9 h-9 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center text-gold text-sm shrink-0">
                                                    {(r.author || "?").charAt(0)}
                                                </span>
                                            )}
                                            <p className="font-body text-sm text-ivory truncate">
                                                {r.author || "Anónimo"}
                                            </p>
                                        </div>
                                        <StarRow value={r.rating} size={12} />
                                    </div>
                                    <p className="font-editorial italic text-[0.65rem] tracking-[0.2em] uppercase text-stone mt-3">
                                        {r.relative_time || formatDate(r.publish_time)}
                                    </p>
                                    <Quote
                                        size={16}
                                        className="text-gold/40 mt-5 -mb-1"
                                        strokeWidth={1.5}
                                        aria-hidden
                                    />
                                    <p className="font-body text-sm md:text-[0.95rem] text-ivory leading-relaxed mt-2 line-clamp-6">
                                        {r.text}
                                    </p>
                                </motion.article>
                            ))}
                        </div>

                        {data.google_maps_uri && (
                            <div className="text-center mt-12 md:mt-14">
                                <a
                                    href={data.google_maps_uri}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="inline-flex items-center gap-2 text-gold font-editorial italic text-sm link-underline"
                                    data-testid="google-reviews-link"
                                >
                                    Leer todas las reseñas en Google Maps{" "}
                                    <ExternalLink size={13} />
                                </a>
                            </div>
                        )}
                    </>
                )}

                {/* Curated strip — always visible on Home (works both
                    standalone when Google fails AND complementing it). */}
                <CuratedStrip />
            </div>
        </section>
    );
}
